import { ActivatedRoute, Router } from "@angular/router";
import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
  ChangeDetectorRef,
} from "@angular/core";
import { map, Observable, of } from "rxjs";
import { combineLatest, switchMap, takeWhile, take, tap, filter } from "rxjs";
import { select, Store } from "@ngrx/store";
import { IColumnDefinition } from "../../common/models/column-definition";
import { IResizedColumn, ITable } from "../../common/models/table";
import { RouteContextService } from "../../context/services/route-context.service";
import { IContext } from "../../context/models/context";
import { WidgetComponent } from "../../dashboards/components/widget-component/widget-component";
import { ConfirmationService } from "primeng/api";
import * as tenancyScheduleTable from "../actions/tenancy-schedule-table";
import * as tenancyScheduleTableColumns from "../actions/tenancy-schedule-table-columns";
import * as tenancyScheduleSelectors from "../selectors/tenancy-schedule-table";
import * as tenancyScheduleColumnSelectors from "../selectors/tenancy-schedule-table-columns";
import { QueryBuilderConfig, RuleSet } from "ngx-angular-query-builder";
import * as filterUtils from "@common/filter/filter-utils";
import * as filterActions from "@common/filter/actions/filter.actions";
import { FilterEditorDialogResult } from "@common/filter/models/filter-editor-dialog-result";
import { getEffectiveProduct } from "@feature/user-settings/selectors/user-settings";
import { tenancySchedulePrintLayoutCss } from '../components/tenancy-schedule-print-layout.css';
import { EntityKeyService } from '@feature/pma-entity/services/entity-key.service';
import { PmaEntityLookupHttpService } from '@feature/pma-entity/services/pma-entity-lookup-http.service';
import { emptyEntityKey, EntityLookupItemDto } from '@feature/pma-entity/pma-entity-types';
import { TableFacadeService } from '@feature/_app_modules/shared/table/services/table-facade.service';

@Component({
  selector: "mojo-tenancy-schedule",
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: "./tenancy-schedule.component.html",
})
export class TenancyScheduleComponent extends WidgetComponent
  implements OnInit, OnDestroy {
  table$: Observable<ITable>;
  loading$: Observable<boolean>;
  error$: Observable<string>;
  availableColumns$: Observable<IColumnDefinition[]>;
  isAlive: boolean = true;
  context: IContext;
  printData$: Observable<any>;
  css: string[] = [tenancySchedulePrintLayoutCss];

  @ViewChild('dataTable') dataTable: any;

  sysAdminFilterText$: Observable<string[]>;
  queryConfig: Observable<QueryBuilderConfig> = of({ fields: {} });
  currentFilter: RuleSet = filterUtils.getDefaultFilter();
  footerValues$: Observable<any>;
  widgetId: string;
  widgetConfigId: number;

  constructor(
    protected store: Store<any>,
    protected confirmationService: ConfirmationService,
    route: ActivatedRoute,
    private contextService: RouteContextService,
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly entityKeyService: EntityKeyService,
    private readonly entityLookupService: PmaEntityLookupHttpService,
    private readonly tableFacadeService: TableFacadeService
  ) {
    super(store, confirmationService);
    this.context = this.contextService.getContextFromRoute(route);
  }

  get hasCustomFilter(): boolean {
    return filterUtils.isNotDefaultFilter(this.currentFilter);
  }

  ngOnInit() {
    this.store.dispatch(
      new tenancyScheduleTable.Load(this.context, this.widget),
    );
    const widgetId = this.widget.id.toString();
    
    this.store.dispatch(
      new filterActions.LoadWidgetFilter(
        new filterActions.WidgetFilterActionPayload(this.widget.id),
      ),
    );

    this.store.dispatch(new tenancyScheduleTableColumns.Load(this.widget));

    this.availableColumns$ = this.store.pipe(
      select(
        tenancyScheduleColumnSelectors.getAvailableColumns(
          this.widget.id.toString(),
        ),
      ),
    );
    this.error$ = this.store.pipe(
      select((state) => state.tenancySchedule.error),
    );
    this.loading$ = this.store.pipe(
      select((state) => state.tenancySchedule.loading),
    );
    const effectiveProduct = this.store.pipe(
      select(getEffectiveProduct),
      takeWhile(() => this.isAlive),
    );

    this.table$ = effectiveProduct.pipe(switchMap((product) => {
      return this.store.pipe(
        select(
          tenancyScheduleSelectors.getFilteredTenancySchedulesWithDataKey(
            this.widget.id.toString(),
            tenancyScheduleSelectors
              .makeTenancyScheduleFilterFieldProviderCallback(),
          ),
        ),
      );
    }));

    const productSource = combineLatest([effectiveProduct, this.table$]);
    this.queryConfig = productSource.pipe(
      switchMap(([product, table]: [string, ITable]) => {
        const callbackFunction = tenancyScheduleSelectors
          .makeTenancyScheduleFilterFieldProviderCallback();
        return this.store
          .pipe(
            select(
              tenancyScheduleSelectors.getQueryBuilderConfig(
                widgetId,
                callbackFunction,
              ),
            ),
            takeWhile(() => this.isAlive),
          );
      }),
    );

    this.store.pipe(
      select(tenancyScheduleSelectors.getUserFilter(widgetId)),
      takeWhile(() => this.isAlive),
    ).subscribe((filter) => {
      this.currentFilter = filter;
    });

    const entityLookupObservable = this.entityKeyService.getEntityKey().pipe(take(1), switchMap(x => {
      if (x != emptyEntityKey) return this.entityLookupService.byKey(x);
      return of(null)

    }));
    const tableDataObservable = this.table$.pipe(
      takeWhile(() => this.isAlive)
    );
    this.printData$ = combineLatest([entityLookupObservable, tableDataObservable]).pipe(map(([entity, table]) => {
      if (table && table.Columns && table.Rows) {
        const filteredColumns = table.Columns.filter(col => !col.IsSystemColumn)
       return {
          entityInfo: this.buildEntityInfo(this.context, entity ? entity['Name'] : ''),
          Columns: filteredColumns.map(col => ({
            ...col,
            // Ensure proper type detection for formatting
            type: this.getColumnType(col)
          })),
          Rows: table.Rows.map(row => {
            // Transform any date strings to Date objects
            const transformedRow = { ...row };
            filteredColumns.forEach(col => {
              if (this.isDateColumn(col) && transformedRow[col.Name]) {
                transformedRow[col.Name] = new Date(transformedRow[col.Name]);
              }
            });
            return transformedRow;
          })
        };
      }
    }));

    this.widgetId = this.widget.id.toString();
    this.widgetConfigId = this.widget.widgetConfigId;
    this.footerValues$ = this.tableFacadeService.getSumValues(this.widgetId, this.widgetConfigId);
  }
     
   
  

  ngOnDestroy(): void {
    this.isAlive = false;
  }

  columnsReordered(selectedColumns: string[]) {
    this.store.dispatch(
      new tenancyScheduleTableColumns.ReorderColumns(
        selectedColumns,
        this.widget,
      ),
    );
    this.store.dispatch(
      new tenancyScheduleTable.Load(this.context, this.widget),
    );
  }

  dialogSaved(selectedColumns: IColumnDefinition[]) {
    this.store.dispatch(
      new tenancyScheduleTableColumns.UpdateColumns(
        selectedColumns,
        this.context,
        this.widget,
      ),
    );
  }

  resetColumns() {
    this.store.dispatch(
      new tenancyScheduleTableColumns.ResetToDefault(this.context, this.widget),
    );
  }

  filterEdited(result: FilterEditorDialogResult): void {
    this.store.dispatch(
      new filterActions.UpdateWidgetFilter(
        new filterActions.WidgetFilterActionPayload(
          this.widget.id,
          result.filter,
        ),
      ),
    );
    this.store.dispatch(
      new tenancyScheduleTable.Load(this.context, this.widget),
    );
  }

  columnsResized(resizedColumn: IResizedColumn) {
    this.store.dispatch(
      new tenancyScheduleTableColumns.ResizeColumns(resizedColumn, this.widget),
    );
  }

  private isDateColumn(col: any): boolean {
    const header = col.Header.toLowerCase();
    return header.includes('date');
  }

  private getColumnType(col: any): number {
    const header = col.Header.toLowerCase();
    if (this.isDateColumn(col)) return 3; // Date type
    if (header.includes('amount') || header.includes('balance')) return 2; // Number type
    return 1; // String type
  }

  private buildEntityInfo(ctx: any, name: string): string | null {
    console.log('buildEntityInfo received:', { ctx, name }); 
    if (!ctx) { 
      return null; 
    }
  
    const entityMap: Record<string, [string, string?]> = {
      UnitRef: ['Unit'],
      PropertyRef: ['Property'],
      EstateRef: ['Estate'],
      UserEnteredClientRef: ['Client'],
      UserEnteredClientParentRef: ['Client Parent'],
      TenantRef: ['Tenant'],
      ManagerCode: ['Manager']
    };
  
    for (const refKey of Object.keys(entityMap)) {
      const ref = ctx[refKey];
      if (ref !== undefined) {
        const result = `${entityMap[refKey][0]}: ${name ? '   ' + name : ''}`;
        console.log('buildEntityInfo returning:', result); 
        return result;
      }
    }
    return null;
  }
}
